import { useContext, useState, useEffect } from 'react';
import {
  ListSubheader,
  Box,
  List,
  styled,
  Button,
  ListItem,
  useTheme,
} from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import { SidebarContext } from 'src/Context/SidebarContext';
import menuConfig from 'src/config/menu';
import { useTranslation } from 'react-i18next';
import { passesMenuAccessGate, getNavPermissionContext } from 'src/Common/permissions/cloudPosPermissions';
import {
  isBillingRestricted,
  isPathAllowedDuringBillingRestriction,
} from 'src/Common/navigation/billingRestriction';
import { modernMenuIcon } from 'src/theme/modernMenuIcons';

// ── "Full Modern" theme tokens (must match the Modern Dashboard launcher) ────
// Every modern rule below is reached ONLY when the active scheme sets isModern
// (ModernTheme / ModernDarkTheme). On Classic the original CSS is returned
// verbatim, so existing clients see zero difference.
const MODERN_ACCENT_GRADIENT_FALLBACK = 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)';
// Active-pill gradient follows the company palette chosen by the developer.
const accentGrad = (theme: any): string =>
  theme?.modernAccentGradient || MODERN_ACCENT_GRADIENT_FALLBACK;

const MenuWrapper = styled(Box)(({ theme }) => {
  const isModern = Boolean((theme as any).isModern);

  if (isModern) {
    return `
  .MuiList-root {
    padding: ${theme.spacing(0.5, 0)};
    margin-bottom: ${theme.spacing(0.5)};
  }

  .MuiListSubheader-root {
    text-transform: uppercase;
    font-weight: 700;
    font-size: ${theme.typography.pxToRem(10.5)};
    color: ${(theme as any).sidebar.menuItemHeadingColor};
    background: transparent;
    padding: ${theme.spacing(1.75, 2.5, 0.75)};
    letter-spacing: 1.2px;
    line-height: 1.5;
  }
`;
  }

  return `
  .MuiList-root {
    padding: ${theme.spacing(0.5, 0.5)};
    margin-bottom: ${theme.spacing(0.25)};
  }

  .MuiListSubheader-root {
    text-transform: uppercase;
    font-weight: 600;
    font-size: ${theme.typography.pxToRem(11)};
    color: ${(theme as any).sidebar.menuItemHeadingColor};
    padding: ${theme.spacing(1, 2.5, 0.5)};
    letter-spacing: 0.5px;
    opacity: 0.7;
  }
`;
});

const SubMenuWrapper = styled(Box)(({ theme }) => {
  const isModern = Boolean((theme as any).isModern);

  if (isModern) {
    // Rounded "pill" navigation with a small tinted icon tile on every row —
    // the sidebar surface is the dark violet gradient in BOTH modern variants,
    // so the light text/white active state reads correctly in light and dark.
    return `
  .MuiListItem-root {
    padding: 0;
    margin-bottom: 0;

    .MuiButton-root {
      color: ${(theme as any).sidebar.menuItemColor};
      transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease,
        box-shadow 0.2s ease;
      width: calc(100% - 20px);
      margin: 2px 10px;
      justify-content: flex-start;
      padding: ${theme.spacing(0.6, 1.15)};
      border-radius: 12px;
      font-size: ${theme.typography.pxToRem(13.5)};
      font-weight: 500;
      letter-spacing: 0.1px;
      text-transform: none;
      border-left: none;

      .MuiButton-startIcon {
        width: 28px;
        height: 28px;
        min-width: 28px;
        border-radius: 9px;
        background: ${(theme as any).sidebar.menuItemIconBg || 'rgba(255,255,255,0.08)'};
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-left: 0;
        margin-right: ${theme.spacing(1.25)};
        transition: background 0.2s ease;
      }

      .MuiSvgIcon-root {
        margin-right: 0;
        font-size: ${theme.typography.pxToRem(17)};
        color: ${(theme as any).sidebar.menuItemIconColor};
        transition: color 0.2s ease;
      }

      &.active {
        color: #ffffff;
        background: ${accentGrad(theme)};
        font-weight: 700;
        box-shadow: 0 8px 18px -8px rgba(124,58,237,.65);

        .MuiButton-startIcon {
          background: rgba(255,255,255,0.22);
        }

        .MuiSvgIcon-root {
          color: #ffffff;
        }
      }

      &:hover:not(.active) {
        color: ${(theme as any).sidebar.menuItemHoverColor || '#ffffff'};
        background: ${(theme as any).sidebar.menuItemHoverBg || 'rgba(255,255,255,0.08)'};
        transform: translateX(3px);

        .MuiButton-startIcon {
          background: ${(theme as any).sidebar.menuItemBgActive};
        }

        .MuiSvgIcon-root {
          color: ${(theme as any).sidebar.menuItemHoverColor || '#ffffff'};
        }
      }
    }
  }
`;
  }

  return `
  .MuiListItem-root {
    padding: ${theme.spacing(0.25, 1)};
    margin-bottom: ${theme.spacing(0.25)};

    .MuiButton-root {
      color: ${(theme as any).sidebar.menuItemColor};
      transition: all 0.18s ease;
      width: 100%;
      justify-content: flex-start;
      padding: ${theme.spacing(0.75, 1.5)};
      border-radius: ${theme.general.borderRadius};
      font-size: ${theme.typography.pxToRem(13.5)};
      font-weight: 500;
      text-transform: none;
      border-left: 2.5px solid transparent;

      .MuiSvgIcon-root {
        margin-right: ${theme.spacing(1.5)};
        font-size: ${theme.typography.pxToRem(19)};
        color: ${(theme as any).sidebar.menuItemIconColor};
        transition: color 0.18s ease;
      }

      &.active {
        color: ${(theme as any).sidebar.menuItemColorActive};
        background: ${(theme as any).sidebar.menuItemBgActive};
        font-weight: 600;
        border-left-color: ${(theme as any).sidebar.menuItemIconColorActive};

        .MuiSvgIcon-root {
          color: ${(theme as any).sidebar.menuItemIconColorActive};
        }
      }

      &:hover:not(.active) {
        color: ${(theme as any).sidebar.menuItemColorActive};
        background: rgba(228,90,13,0.07);
        border-left-color: rgba(228,90,13,0.5);

        .MuiSvgIcon-root {
          color: ${(theme as any).sidebar.menuItemColorActive};
        }
      }
    }
  }
`;
});

function SidebarMenu() {
  const { t } = useTranslation();
  const theme = useTheme();
  // Visual-only switch: true only under the "Full Modern" schemes, undefined on Classic.
  const isModern = Boolean((theme as any).isModern);
  const { closeSidebar, defaultPOSTerminal } = useContext(SidebarContext);
  const [roles, setRoles] = useState<string[]>([]);
  const { lookup: permLookup, bypass: permBypass } = getNavPermissionContext();

  useEffect(() => {
    const storedRoles = localStorage.getItem('userRoles');
    if (storedRoles) {
      try {
        const parsedRoles = storedRoles.split(',').map((role) => role.trim());
        setRoles(parsedRoles);
      } catch (error) {
        console.error('Failed to parse roles from localStorage:', error);
      }
    }
  }, []);

  // Visibility is decided by the shared gate (see passesMenuAccessGate): catalog-gated items show
  // when the granted permission OR the legacy role whitelist matches, company owners/admins always
  // see every non-developer item, and developer tooling stays restricted to Developers. This is why
  // a custom company role (e.g. "Inventory Manager") that was granted Main:Dashboard now sees the
  // Dashboard even though its name is not in the hard-coded whitelist.
  const passesAccessGate = (item: (typeof menuConfig)[number]['items'][number]) =>
    passesMenuAccessGate(item, roles, permLookup, permBypass);

  return (
    <MenuWrapper>
      {menuConfig
        // First filter out groups with no accessible items
        .filter((group) => group.items.some(passesAccessGate))
        // Then map through remaining groups
        .map((group) => (
          <List
            key={group.subheader}
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t(group.subheader)}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              {group.items
                .filter(passesAccessGate)
                .filter((item) => {
                  // Filter POS terminals based on company setting (from API via SidebarContext)
                  if (item.title === 'menu.salesTerminal' && defaultPOSTerminal !== 'Simple') {
                    return false;
                  }
                  if (item.title === 'menu.terminal3' && defaultPOSTerminal !== 'Terminal3') {
                    return false;
                  }
                  return true;
                })
                .map((item) => {
                  // Translate menu item title
                  let displayTitle = t(item.title);
                  // Special handling for Sales Terminal -> Simple POS Terminal
                  if (item.title === 'menu.salesTerminal') {
                    displayTitle = t('menu.simplePosTerminal');
                  } else if (item.title === 'menu.terminal3') {
                    displayTitle = t('menu.touchSaleTerminal');
                  }

                  // Determine data-tour attribute based on menu item
                  const getDataTourAttr = (title: string) => {
                    const tourMapping: { [key: string]: string } = {
                      'menu.salesTerminal': 'sales-terminal',
                      'menu.terminal3': 'terminal-3',
                      'menu.dashboard': 'dashboard',
                      'menu.items': 'items',
                      'menu.purchaseInventory': 'purchases',
                      'menu.salesHistory': 'reports',
                      'menu.setup': 'setup'
                    };
                    return tourMapping[title] || null;
                  };

                  const dataTourAttr = getDataTourAttr(item.title);

                  const navLocked =
                    isBillingRestricted() && !isPathAllowedDuringBillingRestriction(item.path);

                  // Full Modern swaps the classic TwoTone icons for solid Rounded ones so
                  // the nav matches the Modern Dashboard. Unmapped paths keep their own icon.
                  const navIcon =
                    (isModern && modernMenuIcon(item.path)) || item.icon;

                  return (
                    <ListItem component="div" key={item.title}>
                      {navLocked ? (
                        <Button
                          disabled
                          disableRipple
                          fullWidth
                          startIcon={navIcon}
                          sx={{
                            justifyContent: 'flex-start',
                            opacity: 0.45,
                            '&.Mui-disabled': { opacity: 0.45 },
                            ...(isModern
                              ? {
                                  borderRadius: '12px',
                                  '& .MuiButton-startIcon': {
                                    background: 'rgba(255,255,255,0.05)',
                                  },
                                }
                              : {}),
                          }}
                          {...(dataTourAttr ? { 'data-tour': dataTourAttr } : {})}
                        >
                          {displayTitle}
                        </Button>
                      ) : (
                        <Button
                          disableRipple
                          component={RouterLink}
                          onClick={closeSidebar}
                          to={item.path}
                          startIcon={navIcon}
                          {...(dataTourAttr ? { 'data-tour': dataTourAttr } : {})}
                        >
                          {displayTitle}
                        </Button>
                      )}
                    </ListItem>
                  );
                })}
            </SubMenuWrapper>
          </List>
        ))}
    </MenuWrapper>
  );
}

export default SidebarMenu;